import React, { useState } from 'react';
import { 
  Webhook, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Send,
  AlertTriangle,
  AlertCircle,
  Info,
  Bell
} from 'lucide-react';
import { WebhookConfig } from '../../utils/webhook';

interface WebhookSettingsProps {
  config: WebhookConfig;
  isLoading: boolean;
  lastResult: { success: boolean; error?: string; timestamp?: Date } | null;
  onUpdateConfig: (config: Partial<WebhookConfig>) => void;
  onTestWebhook: () => Promise<void>;
}

export function WebhookSettings({
  config,
  isLoading,
  lastResult,
  onUpdateConfig,
  onTestWebhook,
}: WebhookSettingsProps) {
  const [url, setUrl] = useState(config.url);

  const handleSaveUrl = () => {
    onUpdateConfig({ url });
  };

  const handleToggleEnabled = () => {
    onUpdateConfig({ enabled: !config.enabled });
  };

  const handleToggleTrigger = (level: 'critical' | 'warning' | 'caution' | 'info') => {
    onUpdateConfig({
      triggerOn: {
        ...config.triggerOn,
        [level]: !config.triggerOn[level],
      },
    });
  };

  const handleToggleDetails = () => {
    onUpdateConfig({ includeDetails: !config.includeDetails });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <Webhook className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-slate-200">n8n Webhook Integration</h3>
          <p className="text-sm text-slate-400">
            Trigger automated workflows when alerts are detected
          </p>
        </div>
      </div>

      {/* Enable Toggle */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-200">Enable Webhook</p>
            <p className="text-xs text-slate-400 mt-1">
              Automatically send alerts to your n8n workflow
            </p>
          </div>
          <button
            onClick={handleToggleEnabled}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              config.enabled ? 'bg-green-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                config.enabled ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Webhook URL */}
      <div className="card p-4">
        <label className="block text-sm font-medium text-slate-200 mb-2">
          Webhook URL
        </label>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-n8n-instance.com/webhook/..."
            className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg 
                     text-slate-200 placeholder-slate-500 text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleSaveUrl}
            disabled={url === config.url}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-600 
                     disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg 
                     transition-colors"
          >
            Save
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Create a webhook trigger in n8n and paste the URL here
        </p>
      </div>

      {/* Trigger Conditions */}
      <div className="card p-4">
        <p className="text-sm font-medium text-slate-200 mb-3">Trigger On</p>
        <div className="grid grid-cols-2 gap-3">
          <TriggerOption
            label="Critical Alerts"
            icon={<XCircle className="w-4 h-4" />}
            color="text-red-400"
            bgColor="bg-red-500/20"
            checked={config.triggerOn.critical}
            onChange={() => handleToggleTrigger('critical')}
          />
          <TriggerOption
            label="Warning Alerts"
            icon={<AlertTriangle className="w-4 h-4" />}
            color="text-yellow-400"
            bgColor="bg-yellow-500/20"
            checked={config.triggerOn.warning}
            onChange={() => handleToggleTrigger('warning')}
          />
          <TriggerOption
            label="Caution Alerts"
            icon={<AlertCircle className="w-4 h-4" />}
            color="text-orange-400"
            bgColor="bg-orange-500/20"
            checked={config.triggerOn.caution}
            onChange={() => handleToggleTrigger('caution')}
          />
          <TriggerOption
            label="Info Alerts"
            icon={<Info className="w-4 h-4" />}
            color="text-blue-400"
            bgColor="bg-blue-500/20"
            checked={config.triggerOn.info}
            onChange={() => handleToggleTrigger('info')}
          />
        </div>
      </div>

      {/* Include Details */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-200">Include Alert Details</p>
            <p className="text-xs text-slate-400 mt-1">
              Send full alert information (staff names, messages, etc.)
            </p>
          </div>
          <button
            onClick={handleToggleDetails}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              config.includeDetails ? 'bg-green-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                config.includeDetails ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Test Webhook */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-200">Test Webhook</p>
            <p className="text-xs text-slate-400 mt-1">
              Send a test payload to verify your webhook is working
            </p>
          </div>
          <button
            onClick={onTestWebhook}
            disabled={!config.url || isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 
                     disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm 
                     font-medium rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Test
          </button>
        </div>

        {/* Last Result */}
        {lastResult && (
          <div className={`mt-3 p-3 rounded-lg ${
            lastResult.success ? 'bg-green-500/10' : 'bg-red-500/10'
          }`}>
            <div className="flex items-center gap-2">
              {lastResult.success ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400" />
              )}
              <span className={`text-sm ${
                lastResult.success ? 'text-green-400' : 'text-red-400'
              }`}>
                {lastResult.success ? 'Webhook sent successfully!' : lastResult.error}
              </span>
            </div>
            {lastResult.timestamp && (
              <p className="text-xs text-slate-500 mt-1">
                {lastResult.timestamp.toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Last Triggered */}
      {config.lastTriggered && (
        <div className="card p-4">
          <div className="flex items-center gap-2 text-slate-400">
            <Bell className="w-4 h-4" />
            <span className="text-sm">
              Last triggered: {new Date(config.lastTriggered).toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function TriggerOption({
  label,
  icon,
  color,
  bgColor,
  checked,
  onChange,
}: {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      onClick={onChange}
      className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
        checked
          ? `${bgColor} border-current ${color}`
          : 'bg-slate-800/50 border-slate-700 text-slate-400'
      }`}
    >
      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
        checked ? 'bg-current border-current' : 'border-slate-500'
      }`}>
        {checked && <CheckCircle className="w-3 h-3 text-slate-900" />}
      </div>
      {icon}
      <span className="text-sm">{label}</span>
    </button>
  );
}
